// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

// ** Components
import ImageLightbox from './ImageLightbox';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Types
import type { UploadFile } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';
import type { LightboxItem } from './ImageLightbox';

export type ImageTeaserCardItem = {
	/** Image source URL — relative `/public` path or absolute Strapi URL. */
	src?: string;
	/** Alt text for the image */
	alt?: string;
	/** Optional headline */
	headline?: string;
	/** Optional description */
	description?: string;
	/** Optional caption shown in lightbox */
	caption?: string;
	/** Required when `src` is a Strapi-hosted URL. */
	host?: AppContext['host'];
	/** Strapi-hosted URL. */
	imageData: UploadFile;
	/** Hide titel and description if set */
	showOnlyImages?: boolean | null;
};

export type ImageTeaserCardProps = ImageTeaserCardItem & {
	/** All items for lightbox navigation (defaults to just this card) */
	lightboxItems?: LightboxItem[];
	/** Index of this card within lightboxItems */
	lightboxIndex?: number;
	/** Strapi-hosted URL. */
	imageData?: UploadFile;
};

const ImageTeaserCard = ({
	src,
	alt,
	headline,
	description,
	caption,
	host,
	imageData,
	showOnlyImages,
	lightboxItems,
	lightboxIndex = 0
}: ImageTeaserCardProps) => {
	const [open, setOpen] = React.useState(false);
	const [activeIndex, setActiveIndex] = React.useState(lightboxIndex);

	const items: LightboxItem[] = lightboxItems ?? [{ src, alt, caption, imageData }];

	const handleOpen = () => {
		setActiveIndex(lightboxIndex);
		setOpen(true);
	};

	const handleClose = React.useCallback(() => setOpen(false), []);
	const handlePrev = React.useCallback(() => setActiveIndex((i) => Math.max(0, i - 1)), []);
	const handleNext = React.useCallback(() => setActiveIndex((i) => Math.min(items.length - 1, i + 1)), [items.length]);
	const lightboxButtonLabel =
		headline || caption || alt
			? `Bild in Grossansicht oeffnen: ${headline ?? caption ?? alt}`
			: 'Bild in Grossansicht oeffnen';

	return (
		<>
			<Box>
				{/* Image */}
				<ButtonBase
					onClick={handleOpen}
					aria-label={lightboxButtonLabel}
					aria-haspopup="dialog"
					disableRipple
					sx={{
						cursor: 'pointer',
						display: 'block',
						width: '100%',
						borderRadius: '0.5rem',
						overflow: 'hidden',
						textAlign: 'left',
						'&:focusVisible': {
							outline: '2px solid',
							outlineColor: 'common.black',
							outlineOffset: 2
						},
						'&:hover img': {
							transform: 'scale(1.03)'
						},
						'& .MuiTouchRipple-root': { display: 'none' }
					}}
				>
					<Box
						sx={{
							position: 'relative',
							width: '100%',
							aspectRatio: '4 / 3'
						}}
					>
						<StrapiImageRenderer
							src={src}
							host={host}
							imageData={imageData}
							alt={alt ?? headline ?? ''}
							fill
							sizes="(max-width: 899px) 100vw, 50vw"
							style={{
								objectFit: 'cover',
								objectPosition: 'center',
								transition: 'transform 0.3s ease'
							}}
						/>
					</Box>
				</ButtonBase>

				{/* Text */}
				{(headline || description) && !showOnlyImages && (
					<Box sx={{ mt: '1.5rem' /* 24px */ }}>
						{headline && (
							<Typography
								variant="title-md"
								sx={(t) => ({
									fontSize: '1.125rem',
									lineHeight: '1.5rem',
									letterSpacing: 0,
									color: 'text.primary',
									mb: description ? '1rem' : 0,
									[t.breakpoints.up('md')]: {
										fontSize: '1.5rem',
										lineHeight: '2rem',
										letterSpacing: 0
									},
									[t.breakpoints.up('lg')]: {
										fontSize: '1.5rem',
										lineHeight: '2rem',
										letterSpacing: 0
									}
								})}
							>
								{headline}
							</Typography>
						)}

						{description && !showOnlyImages && (
							<Typography variant="body-sm" sx={{ color: 'text.secondary' }}>
								{description}
							</Typography>
						)}
					</Box>
				)}
			</Box>

			<ImageLightbox
				open={open}
				items={items}
				host={host}
				activeIndex={activeIndex}
				onClose={handleClose}
				onPrev={handlePrev}
				onNext={handleNext}
			/>
		</>
	);
};

export default ImageTeaserCard;
